import { Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { BehaviorSubject, filter, firstValueFrom, Subject, Subscription } from 'rxjs';
import { CustomModalComponent } from 'src/app/components/custom-modal/custom-modal.component';
import { AuthenticationContants } from 'src/app/constants/authentication-constants';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { WindowEnums } from 'src/app/enum/window-enums';
import { TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { OptionWindowModel } from 'src/app/models/authentication/option-window.model';
import { UserModel } from 'src/app/models/authentication/user.model';
import { CityModel } from 'src/app/models/management/city.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { CityTableModel } from 'src/app/models/tables/management/city-table.model';
import { OptionWindowService } from 'src/app/services/authentication/option-window.service';
import { WindowService } from 'src/app/services/authentication/window.service';
import { UserAuthenticatedService } from 'src/app/services/global-services/user-authenticated.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { CityService } from 'src/app/services/management/city.service';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import { RegisterCityComponent } from '../register-city/register-city.component';

@Component({
  selector: 'app-city',
  templateUrl: './city.component.html',
  styleUrls: ['./city.component.scss'],
  standalone: false
})
export class CityComponent implements OnInit {
  @ViewChild('cityModal') cityModalComponent?: CustomModalComponent | null = null;

  componentRegisterCity = RegisterCityComponent;
  ascendant: boolean = true;
  columnSort: string | null = null;
  filters: ColumnResponseModel[] = [];
  data: PagedResponseModel<CityTableModel> = new PagedResponseModel<CityTableModel>();
  header: ColumnResponseModel[] = [];
  isOpened: string | undefined = undefined;
  inputsForCity$ = new BehaviorSubject<any>({
    cityModel: new CityModel,
  });
  limit: number = 10;
  nameWindow: string = 'Ciudades';
  outputForCityModals$ = new Subject<any>();
  page: number = 1;
  permissionUpdatable: boolean = false;
  permissionsUser: Array<OptionWindowModel> = new Array<OptionWindowModel>();
  projectName = ProjectName.projectName;
  subscription: Subscription;
  tableActions: TableAction[] = [
    {
      label: 'Editar',
      icon: 'pi pi-pencil',
      action: 'edit'
    },
    {
      label: 'Eliminar',
      icon: 'pi pi-trash',
      action: 'delete'
    },
  ];
  url: string = "city";
  userAuthenticated: UserModel = new UserModel();

  constructor(private confirmDialogService: ConfirmDialogService,
    private cityService: CityService,
    private headerService: HeaderService,
    private optionWindowService: OptionWindowService,
    private router: Router,
    private title: Title,
    private toastHandlerService: ToastHandlerService,
    private userAuthenticatedService: UserAuthenticatedService,
    private windowService: WindowService) { }

  async ngOnInit() {
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    const filters = localStorage.getItem(this.url);
    if (filters) this.filters = JSON.parse(filters);
    this.headerService.setTitle(this.nameWindow);
    await Promise.all([
      this.getColumnsForConsultation(),
      this.getAllCitiesWithPagination(),
    ]);
    this.getPermission();
    this.subscription = this.inputsForCity$.subscribe(async ({ type }) => {
      switch (type) {
        case 'reload':
          await this.getAllCitiesWithPagination();
          break;
        default:
          console.warn('Tipo de evento desconocido:', type);
      }
    });
  }

  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    this.ngOnDestroy();
  }

  async ngOnDestroy() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      const window = windowsOpened.findIndex(x => x === this.url);
      if (window != -1) {
        windowsOpened.splice(window, 1);
        localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      }
    }

    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  async deleteCity(city: CityTableModel) {
    if (await this.confirmDialogService.confirm(`¿Realmente desea eliminar la ciudad ${city.name}?`)) {
      try {
        await firstValueFrom(this.cityService.deleteCity(city.cityId));
        this.toastHandlerService.showToast(
          'success',
          'La ciudad fue eliminado exitosamente.'
        );
        this.getAllCitiesWithPagination();
      } catch (error) {
        console.error('Error al eliminar la ciudad.', error);
      }
    }
  }

  async getColumnsForConsultation() {
    try {
      if (this.header.length === 0) {
        this.header = await firstValueFrom(this.cityService.getColumnsForCitySelection());
      }
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta.', error);
    }
  }

  async getAllCitiesWithPagination() {
    try {
      this.data = await firstValueFrom(this.cityService.getCitiesForSelection(
        this.page, this.limit, this.columnSort, this.ascendant, this.filters
      ));
    } catch (error) {
      console.error('Error al buscar las ciudades.', error);
    }
  }

  async getPermission() {
    let url = this.router.url as string
    url = url.substring(1);
    this.url = url;
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      this.isOpened = windowsOpened.find(x => x === this.url);
      windowsOpened.push(this.url);
      localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      if (this.isOpened) {
        this.toastHandlerService.showToast(
          'error',
          `No se permite tener dos ventanas/pestañas de ${this.nameWindow} abiertas.`
        );
      }
    } else {
      localStorage.clear();
    }
    try {
      this.permissionUpdatable = await firstValueFrom(this.windowService.getPermissionProfileByUrl(this.url));
    } catch (error) {
      console.error('Error al buscar los permisos por ciudad.', error);
    }
    this.getPermissionsForUserByWindow();
  }

  async getPermissionsForUserByWindow() {
    this.userAuthenticated = await firstValueFrom(
      this.userAuthenticatedService.userObservable.pipe(
        filter(user => user && user.userId !== 0)
      )
    );
    this.permissionsUser = await firstValueFrom(this.optionWindowService.getPermissionsForUserByWindow(WindowEnums.City));
  }

  openModalCity() {
    if (!this.validations()) return;

    this.updateInput(new CityModel);
  }

  onAction(event: { action: string, item?: CityTableModel, selectedItems?: number[] }) {
    if (!this.validations()) return;

    if (event.action === 'edit' && event.item) {
      const cityModel = new CityModel;
      cityModel.cityId = event.item.id;
      cityModel.departmentId = event.item.departmentId;
      cityModel.code = event.item.code;
      cityModel.name = event.item.name;
      this.updateInput(cityModel)
    }
    if (event.action === 'delete' && event.item) {
      this.deleteCity(event.item);
    }
  }

  async onFilterChange(event: { filters: any }) {
    const filters = event.filters.map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index).map((item: any) => item.value);
    this.filters = this.removeDuplicates(filters);
    this.filters.forEach((filter: any) => this.processFilter(filter));
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    await this.getAllCitiesWithPagination();
  }

  processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0) {
      this.filters = this.filters.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filters.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  reloadList(reload: boolean) {
    if (reload) {
      this.getAllCitiesWithPagination();
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }

  updateInput(cityContract: CityModel) {
    this.inputsForCity$.next({
      cityModel: cityContract,
    });
    this.cityModalComponent && this.cityModalComponent.show();
  }

  validations() {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast('warning', 'El usuario no tiene permisos para modificar las ciudades. Comuníquese con el administrador del sistema', 'Atención');
      return false;
    }

    return true;
  }
}
